<template>

    <div id="form-container" ref="formContainer">
        <b-container fluid>
            <h1>Create Account</h1>
            <b-row>
                <b-col>
                    <p>
                        Please enter the following personal and contact information.
                    </p>
                </b-col>
            </b-row>

            <b-form class="mt-3">
                <h2>
                    Personal Information
                </h2>
                <b-row>
                    <b-col cols="4">
                        <BaseTextInput v-model="profile.name.firstName"
                                       :v="$v.profile.name.firstName"
                                       slug="firstName"
                                       label="First name"
                        />
                    </b-col>
                    <b-col cols="4">
                        <BaseTextInput v-model="profile.name.middleName"
                                       :v="$v.profile.name.middleName"
                                       slug="middleName"
                                       label="Middle name"
                        />
                    </b-col>
                    <b-col cols="4">
                        <BaseTextInput v-model="profile.name.lastName"
                                       :v="$v.profile.name.lastName"
                                       slug="lastName"
                                       label="Last name"
                        />
                    </b-col>
                </b-row>

                <h2>Contact Information</h2>
                <b-row>
                    <b-col cols="4">
                        <BaseMaskedTextInput v-model="profile.phone"
                                             :v="$v.profile.phone"
                                             slug="phone"
                                             label="Cell phone number"
                                             setId="phone"
                                             mask="(###)###-####"
                                             :plaintext="noPhone"
                        />
                    </b-col>
                    <b-col cols="4">
                        <BaseTextInput v-model="profile.email"
                                       :v="$v.profile.email"
                                       slug="email"
                                       label="Email"
                        />
                    </b-col>
                </b-row>
                <b-row>
                  <b-col cols="4">
                    <BaseCheckboxInput label="Check the box if you do not have a phone number"
                                       v-model="noPhone"
                                       :v="$v.noPhone"
                                       slug="noPhone2"
                                       setId="noPhone2"
                    />
                  </b-col>
                  <b-col cols="4">
                    <p>The email address you enter will be assigned as your account username.</p>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="4">
                    <BaseTextInput v-model="profile.address.streetAddress"
                                   :v="$v.profile.address.streetAddress"
                                   slug="address"
                                   label="Address Line 1"
                                   maxlength="90"
                    />
                  </b-col>
                  <b-col lg="4">
                    <BaseTextInput v-model="profile.address.poBox"
                                   :v="$v.profile.address.poBox"
                                   slug="poBox"
                                   setId="poBox"
                                   label="Address Line 2"
                                   maxlength="15"
                    />
                  </b-col>
                </b-row>
                <b-row>
                  <b-col lg="3" v-if="!isMn">
                    <BaseTextInput v-model="profile.address.city"
                                   :v="$v.profile.address.city"
                                   slug="addressCity"
                                   label="City"
                                   maxLength="100"
                    />
                  </b-col>
                  <b-col lg="3" v-if="isMn">
                    <BaseSearchableSelector v-model="profile.address.mnCity"
                                            :v="$v.profile.address.mnCity"
                                            label="City"
                                            :options = cityOptions
                    />
                  </b-col>
                  <b-col lg="3">
                    <BaseSelector v-model="profile.address.state"
                                  :v="$v.profile.address.state"
                                  label="State"
                                  slug="addressState"
                                  :options=opts.stateOptions
                    />
                  </b-col>
                  <b-col lg="3">
                    <BaseMaskedTextInput v-model="profile.address.zip"
                                         :v="$v.profile.address.zip"
                                         slug="zip"
                                         label="Zip code"
                                         mask="#####-####"
                    />
                  </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <BaseTextInput v-model="profile.license"
                                       :v="$v.profile.license"
                                       slug="license"
                                       label="Attorney license number"
                                       setId="attorneyLicenseNumber"
                                       tip="If you are a licensed Minnesota Attorney, please enter your license number."
                                       maxlength="25"/>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-button type="button" variant="primary" @click="register">Register</b-button>
                    </b-col>
                </b-row>
            </b-form>
        </b-container>

    </div>
</template>

<script>
import axios from "axios"
import axiosRetry from 'axios-retry'
import {required, minLength, maxLength, email, helpers, requiredIf} from 'vuelidate/lib/validators'
import {toastService} from "@/mixins/toastService";
import {commonOptions} from "@/mixins/commonOptions";
import LocalStorageService from "@/common/LocalStorageService";
import BaseCheckboxInput from "@/components/templates/input/BaseCheckboxInput.vue";
import BaseTextInput from "@/components/templates/input/BaseTextInput.vue";
import BaseSelector from "@/components/templates/input/BaseSelector.vue";
import BaseMaskedTextInput from "@/components/templates/input/BaseMaskedTextInput.vue";
import BaseSearchableSelector from "@/components/templates/input/BaseSearchableSelector.vue";


axiosRetry(axios, { retries: 0,
    retryDelay: axiosRetry.exponentialDelay  })

const localStorageService = LocalStorageService.getService();
const patternCheck = helpers.regex('pattern', /^[a-zA-Z0-9\s]+$/)

export default {
  components: {BaseSearchableSelector, BaseMaskedTextInput, BaseSelector, BaseTextInput, BaseCheckboxInput},
    data() {
        return {
            profile: {
                address: {
                  streetAddress: null,
                  poBox: null,
                  city: null,
                  mnCity: null,
                  state: null,
                  zip: null,
                  country: 'US',
                  addressType: "CONTACT",
                },
                name: {
                    lastName: null,
                    firstName: null,
                    middleName: null,
                },
                phone: null,
                email: null,
                license: null,
                orders: []
            },
            cityOptions: [],
            redirect: null,
            noPhone: false,
        }
    },
    mixins: [
        toastService,
        commonOptions
    ],
    validations() {
        return {
            profile: {
                address: {
                  streetAddress: { required },
                  poBox: { required: false },
                  city: { required: requiredIf(function(){ return this.profile?.address?.state !== 'MN'}) },
                  mnCity: { required: requiredIf(function(){ return this.profile?.address?.state === 'MN'}) },
                  state: { required },
                  zip: { required },
                  addressType: { required: requiredIf(function(){ return this.isDeathCategory }) }
                },
                phone: { required: requiredIf(function() { return this.hasPhone; }),
                    minLength: minLength(13),
                    maxLength: maxLength(13) },
                email: { required, email },
                name: {
                    firstName: { required },
                    lastName: { required },
                    middleName: { required: false },
                },
                license: { required:false,
                    maxLength: maxLength(25),
                    patternCheck },
                orders: { required: false },
            },
            noPhone: {required : false }
        }
    },
    watch: {
      noPhone() {
        if (this.noPhone) {
          this.profile.phone = null
        }
      },
      stateVal() {
        if (!this.profile.address) {
          this.profile.address = {
            streetAddress: null,
            poBox: null,
            city: null,
            mnCity: null,
            state: null,
            zip: null,
            country: "US",
            addressType: "CONTACT",
            international: false,
          }
        }
        if(this.isMn) {
          let exists = this.cityOptions.filter(c => c.label.toUpperCase() === this.profile.address.city?.toUpperCase())
          if (exists.length > 0) {
            this.profile.address.mnCity = exists[0]
          } else {
            this.profile.address.mnCity = null
          }
          this.profile.address.city = null
        } else if (!this.isMn && this.profile?.address?.mnCity && this.profile?.address?.mnCity?.label) {
          this.profile.address.city = this.profile.address.mnCity.label
          this.profile.address.mnCity = null
        }
      },
    },
    methods: {
        register() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                this.$nextTick(() => {
                    document.getElementById('form-container').scrollIntoView()
                })
                return
            }
            if (localStorageService.getOrderNumber()) {
                this.profile.orders = [{id: localStorageService.getOrderNumber()}]
            }
            axios.post(`${process.env.VUE_APP_API_BASE_URL}/profile/register`,
                this.profile)
                .then(response => {
                    if(response.status === 200) {
                        toastService.showRootToast("Success", "You have registered your account.", "success", this.$root)
                        if (this.redirect){
                          this.login()
                        } else {
                          setTimeout( () => this.$router.push({ path: '/home'}), 1000)
                        }
                    } else if (response.status === 409) {
                        toastService.showRootToast("Error", "An account has already been registered with this email.", "danger", this.$root)
                    } else {
                        toastService.showRootToast("Error", "There was an error registering your account.", "danger", this.$root)
                    }
                })
                .catch(error => {
                    toastService.showRootToast("Error", "An error has occurred while processing your request. Please try again later or contact MDH.", "danger", this.$root)
                })
        },
        loadLicense() {
            axios.get(`${process.env.VUE_APP_API_BASE_URL}/order/license/` + localStorageService.getOrderNumber())
                .then(response=>{
                    this.profile.license = response.data
                })
        },
        login() {
          this.$keycloak.login({'redirectUri': `${process.env.VUE_APP_KEYCLOAK_BASE_URI}${this.redirect}`})
        },
        loadCities(){
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/options/city/known`)
              .then(response=>{
                this.cityOptions = response.data
              })
        },
    },
    created() {
        document.title = "Create Account - Online Storefront"
    },
    computed: {
      hasPhone(){
        return !this.noPhone
      },
      isMn() {
        return this.profile?.address?.state === 'MN'
      },
    },
    mounted() {
        this.loadCities()
        if (localStorageService.getOrderNumber() != null) {
            this.loadLicense()
        }
        this.redirect = this.$route.params.redirect
    }
}
</script>
